import './kp-pdf-document.less';

import type {PDFPageProxy} from 'pdfjs-dist/types/src/display/api';
import type {IAugmentedJQuery, IOnDestroy, IPostLink, IScope} from 'angular';
import type {KpPdfDataComponentController} from '../kp-pdf-data/kp-pdf-data.component';
import KpPdfDataComponent from '../kp-pdf-data/kp-pdf-data.component';
import {ngComponentCssClass} from 'shared/utils/ng-@decorators';
import {throttle} from 'lodash';
import type {PdfScale, PdfViewerFit} from '../kp-pdf-viewer/kp-pdf-viewer.component';
import {ResizeObserver} from '@juggle/resize-observer/lib/ResizeObserver';
import type {Component, OnChanges, OnChangesObject} from 'typings/portaro.fe.types';
import type {PdfError, PdfInfo} from '../pdf-viewer';

export abstract class KpPdfDocumentComponentController implements KpPdfDocumentComponentBindings, IPostLink, OnChanges<KpPdfDocumentComponentBindings>, IOnDestroy {
    public pages: PDFPageProxy[];
    public pageCount = 0;
    public scale: PdfScale;
    public optimalScale: number;
    public fit: PdfViewerFit;
    public currentPage = 1;
    public isPreviewEnabled = false;

    public abstract onScaleChange(locals: { $scale: number; $isOptimal: boolean; $selfChange: boolean });

    public abstract onFitChange(locals: { $fit: PdfViewerFit });

    public pdfDataController: KpPdfDataComponentController;
    private resizeObserver: ResizeObserver;
    private scrollHandler: () => void;
    private scrollTop: number;
    private clientHeight: number;

    /*@ngInject*/
    constructor(private $element: IAugmentedJQuery, private PIXEL_RATIO: number, private VIEWPORT_RATIO: number, private $scope: IScope) {
    }

    public $postLink(): void {
        this.scrollHandler = throttle(() => this.$scope.$apply(() => this.updateScrollBounds()), 300);

        this.$element[0].addEventListener('scroll', this.scrollHandler, true);

        this.resizeObserver = new ResizeObserver((() => {
            this.updateFit(this.fit);
        }));
        this.resizeObserver.observe(this.$element[0]);
    }

    public $onChanges(onChangesObj: OnChangesObject<KpPdfDocumentComponentBindings>): void {
        if (onChangesObj.fit && onChangesObj.fit.currentValue) {
            this.updateFit(onChangesObj.fit.currentValue);
        }

        if (onChangesObj.pageCount && onChangesObj.pageCount.currentValue ||
            onChangesObj.isPreviewEnabled && typeof onChangesObj.isPreviewEnabled.currentValue === 'boolean') {

            this.fitWidth();
        }
    }

    public $onDestroy(): void {
        this.$element[0].removeEventListener('scroll', this.scrollHandler);
        this.resizeObserver.disconnect();
    }

    public onPageErrored(error: PdfError) {
        this.pdfDataController.onPageErrored(error);
    }

    public onPageFocused($pageNumber: number) {
        this.pdfDataController.onPageFocus({$pageNumber});
    }

    public onPageRendered(info: PdfInfo) {
        this.pdfDataController.onPageRendered(info);
    }

    public onPagesFetch(currentPage: number) {
        this.pdfDataController.fetchPages(currentPage);
    }

    public onPageJump(scrollTop: number) {
        this.$element[0].scrollTop = scrollTop;
    }

    public onPagesReset() {
        this.updateFit(this.fit);
        // this.fitWidth();
    }

    public updateScrollBounds() {
        const {scrollTop, clientHeight} = this.$element[0];
        this.scrollTop = scrollTop;
        this.clientHeight = clientHeight;
    }

    private updateFit(fit: string) {
        switch (fit) {
            case 'width':
                this.fitWidth();
                break;

            case 'auto':
                this.fitAuto();
                break;

            default:
                break;
        }
    }

    private pageWidthScale() {
        const {defaultViewport} = this;

        if (!defaultViewport.width) {
            return 0;
        }

        return (this.$element[0].clientWidth * this.PIXEL_RATIO) * this.VIEWPORT_RATIO / defaultViewport.width;
    }

    private pageHeightScale() {
        const {defaultViewport} = this;
        if (!defaultViewport.height) {
            return 0;
        }

        return (this.$element[0].clientHeight * this.PIXEL_RATIO) * this.VIEWPORT_RATIO / defaultViewport.height;
    }

    // and a subjective scale factor based on the screen size.
    private fitWidth() {
        const scale = this.pageWidthScale();
        this.updateScale(scale, !this.optimalScale);
    }

    private fitHeight() {
        const scale = this.isPortrait ? this.pageHeightScale() : this.pageWidthScale();
        this.updateScale(scale, false);
    }

    private fitAuto() {
        const scale = Math.min(this.pageWidthScale(), this.pageHeightScale());

        this.updateScale(scale, false);
    }

    private updateScale(scale: number, isOptimal = false) {
        if (!scale) {
            return;
        }

        this.onScaleChange({$scale: scale, $isOptimal: isOptimal, $selfChange: true});
    }

    get defaultViewport() {

        if (!this.pages.length) {
            return {width: 0, height: 0};
        }
        const [page] = this.pages;

        return page.getViewport({scale: 1.0});
    }

    get isPortrait(): boolean {
        const {width, height} = this.defaultViewport;
        return width <= height;
    }
}

export default class KpPdfDocumentComponent implements Component<KpPdfDocumentComponentBindings> {
    public static componentName = 'kpPdfDocument';

    @ngComponentCssClass('pdf-document')
    public templateUrl = require('./kp-pdf-document.tpl.pug');

    public bindings = {
        pages: '<',
        pageCount: '<?',
        scale: '<?',
        optimalScale: '<?',
        fit: '<?',
        currentPage: '<?',
        isPreviewEnabled: '<?',
        onScaleChange: '&?',
        onFitChange: '&?'
    } as const;
    public controller = KpPdfDocumentComponentController;
    public require = {
        pdfDataController: `^${KpPdfDataComponent.componentName}`
    };
    public controllerAs = 'pdfDocumentController';
}

interface KpPdfDocumentComponentBindings {
    pages: PDFPageProxy[];
    pageCount?: number;
    scale?: PdfScale;
    optimalScale?: number;
    fit?: PdfViewerFit;
    currentPage?: number;
    isPreviewEnabled?: boolean;

    onScaleChange(locals: { $scale: number, $isOptimal: boolean; $selfChange: boolean });

    onFitChange(locals: { $fit: PdfViewerFit });
}
